import React, { useEffect, useRef, useState } from 'react';
import Navbar from '../components/Navbar';
import CourseTabs from '../components/academy/CourseTabs';
import CourseOverview from '../components/academy/CourseOverview';
import CourseModuleContent from '../components/academy/CourseModuleContent';
import CourseFacilitatorGuide from '../components/academy/CourseFacilitatorGuide';
import CourseKnowledgeCheck from '../components/academy/CourseKnowledgeCheck';
import AcademyCertificateView from '../components/academy/AcademyCertificateView';
import { printAcademyCertificate } from '../components/academy/academyCertificatePrint';
import { getPublishedAcademyCourse } from '../services/academyReadService';
import { getCurrentUserEnrollmentForCourse } from '../services/academyEnrollmentService';
import {
  completeLearningUnit,
  getCourseLearningUnitIdentities,
  getEnrollmentUnitProgress
} from '../services/academyProgressService';
import {
  completeEnrollment,
  getEnrollmentCompletion
} from '../services/academyCompletionService';
import {
  getCertificateForCompletion,
  issueCertificate
} from '../services/academyCertificateService';
import { useUserRole } from '../context/UserRoleContext';

// Eksempeldata basert på bildet
const coursesData = [
  {
    id: 1,
    category: 'Grunnleggende',
    title: 'Introduksjon til psykososialt arbeidsmiljø',
    description: 'En grunnleggende innføring i hva som skaper et godt arbeidsmiljø og lovverkets krav.',
    audience: 'Alle',
    price: '1 200,-',
    duration: '2 timer',
    format: 'Digitalt / Hybrid',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 2,
    category: 'Lovverk',
    title: 'Arbeidsmiljøloven i praksis',
    description: 'Gjennomgang av de viktigste paragrafene for ledere og tillitsvalgte.',
    audience: 'Leder/HR',
    price: '2 900,-',
    duration: '4 timer',
    format: 'Digitalt / Hybrid',
    image: 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 3,
    category: 'Kultur',
    title: 'Psykologisk trygghet',
    description: 'Hvordan skape en kultur der alle tør å si sin mening og lære av feil.',
    audience: 'Alle',
    price: '1 500,-',
    duration: '2 timer',
    format: 'Digitalt / Hybrid',
    image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 4,
    category: 'Konflikt',
    title: 'Konfliktmekling',
    description: 'Praktisk tilnærming til håndtering og løsning av fastlåste konflikter på arbeidsplassen.',
    audience: 'Leder/HR',
    price: '3 500,-',
    duration: '6 timer',
    format: 'Fysisk',
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 5,
    category: 'Ledelse',
    title: 'Ledelse og trivsel',
    description: 'Verktøy for ledere som ønsker å bygge motiverte og selvgående team.',
    audience: 'Ledere',
    price: '2 500,-',
    duration: '4 timer',
    format: 'Digitalt / Hybrid',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 6,
    category: 'Samarbeid',
    title: 'Kommunikasjonsferdigheter',
    description: 'Lær teknikkene for klar, tydelig og anerkjennende kommunikasjon i hverdagen.',
    audience: 'Alle',
    price: '1 800,-',
    duration: '3 timer',
    format: 'Digitalt',
    image: 'https://images.unsplash.com/photo-1552581234-26160f608093?auto=format&fit=crop&q=80&w=800'
  }
];

// Unike kategorier for filter-knappene
const categories = [
  'Alle', 'Grunnleggende', 'Lovverk', 'Kultur', 'Konflikt', 'Ledelse',
  'Samarbeid', 'Helse', 'Arbeidsmiljø', 'Teamutvikling', 'Strategi'
];

const COURSE_KEY = 'psykososialt-arbeidsmiljo-praktisk-handling';
const REQUIRED_MODULE_COUNT = 10;

function completionErrorMessage(error) {
  const text = String(error?.message || '').toLowerCase();
  if (text.includes('assessment') && text.includes('passed')) {
    return 'Bestått kunnskapstest kreves før denne modulen kan fullføres.';
  }
  if (text.includes('attendance') || text.includes('live academy')) {
    return 'Bekreftet oppmøte på den tilknyttede liveleveransen kreves før fullføring.';
  }
  return error?.message || 'Modulen kunne ikke markeres som fullført.';
}

function formatCompletionDate(value) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('nb-NO', {
    dateStyle: 'long',
    timeStyle: 'short'
  }).format(date);
}

export const Courses = () => {
  const { role } = useUserRole();
  const canIssueCertificate = role === 'SUPERADMIN' || role === 'COMPANY_ADMIN';
  const [activeFilter, setActiveFilter] = useState('Alle');
  const [course, setCourse] = useState(null);
  const [courseStatus, setCourseStatus] = useState('loading');
  const [courseError, setCourseError] = useState('');
  const [activeTab, setActiveTab] = useState('course');
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [isModuleOpen, setIsModuleOpen] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [unitIdsByKey, setUnitIdsByKey] = useState({});
  const [progressRows, setProgressRows] = useState([]);
  const [progressStatus, setProgressStatus] = useState('loading');
  const [progressError, setProgressError] = useState(null);
  const [busyUnitId, setBusyUnitId] = useState(null);
  const [completionError, setCompletionError] = useState(null);
  const [completionRecord, setCompletionRecord] = useState(null);
  const [completionStatus, setCompletionStatus] = useState('loading');
  const [certificate, setCertificate] = useState(null);
  const [certificateStatus, setCertificateStatus] = useState('loading');
  const [certificateError, setCertificateError] = useState('');
  const [certificatePrintError, setCertificatePrintError] = useState('');
  const [certificateIssueBusy, setCertificateIssueBusy] = useState(false);
  const [certificateViewOpen, setCertificateViewOpen] = useState(false);
  const [verificationSecret, setVerificationSecret] = useState(null);
  const completionLock = useRef(false);
  const completionRpcLock = useRef(false);
  const certificateIssueLock = useRef(false);

  useEffect(() => {
    let isCurrent = true;

    getPublishedAcademyCourse(
      COURSE_KEY,
      {
        version: '1.0',
        languageCode: 'nb'
      }
    )
      .then((publishedCourse) => {
        if (!isCurrent) return;

        const modules = [...(publishedCourse.modules || [])]
          .sort((first, second) => first.displayOrder - second.displayOrder);
        setCourse({ ...publishedCourse, modules });
        setSelectedModuleId((currentId) => (
          modules.some((module) => (module.id || module.unitKey) === currentId)
            ? currentId
            : modules[0]?.id || modules[0]?.unitKey || null
        ));
        setCourseStatus('success');
      })
      .catch((error) => {
        if (!isCurrent) return;
        setCourseError(error?.message || '');
        setCourseStatus('error');
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(() => {
    if (!course) return undefined;

    let isCurrent = true;
    setProgressStatus('loading');
    setCompletionStatus('loading');
    setProgressError(null);
    setCompletionError(null);

    async function loadProgress() {
      try {
        const currentEnrollment = await getCurrentUserEnrollmentForCourse({
          courseKey: COURSE_KEY,
          version: '1.0',
          languageCode: 'nb',
          includeCompleted: true
        });
        const identities = await getCourseLearningUnitIdentities(
          currentEnrollment.courseVersionId
        );
        const progress = await getEnrollmentUnitProgress({
          enrollmentId: currentEnrollment.enrollmentId,
          courseVersionId: currentEnrollment.courseVersionId
        });
        const completion = await getEnrollmentCompletion(
          currentEnrollment.enrollmentId
        );

        if (!isCurrent) return;

        const identityMap = Object.fromEntries(
          identities.map((unit) => [unit.unit_key, unit.id])
        );
        const missingUnit = course.modules.find(
          (module) => !identityMap[module.unitKey]
        );

        if (missingUnit) {
          throw new Error(`Database learning-unit ID is missing for ${missingUnit.unitKey}.`);
        }

        setEnrollment(currentEnrollment);
        setUnitIdsByKey(identityMap);
        setProgressRows(progress);
        setCompletionRecord(completion);
        setCompletionStatus('ready');
        setProgressStatus('ready');
      } catch (error) {
        if (!isCurrent) return;
        setProgressError(error);
        setCompletionStatus('error');
        setProgressStatus('error');
      }
    }

    loadProgress();

    return () => {
      isCurrent = false;
    };
  }, [course]);

  useEffect(() => {
    let isCurrent = true;

    if (!completionRecord?.id) {
      setCertificate(null);
      setCertificateStatus('ready');
      setCertificateError('');
      return () => {
        isCurrent = false;
      };
    }

    setCertificateStatus('loading');
    setCertificateError('');

    getCertificateForCompletion(completionRecord.id)
      .then((existingCertificate) => {
        if (!isCurrent) return;
        setCertificate(existingCertificate);
        setCertificateStatus('ready');
      })
      .catch((error) => {
        if (!isCurrent) return;
        setCertificateError(error?.message || 'Kunne ikke lese kursbeviset.');
        setCertificateStatus('error');
      });

    return () => {
      isCurrent = false;
    };
  }, [completionRecord?.id]);

  const filteredCourses = activeFilter === 'Alle'
    ? coursesData
    : coursesData.filter(course => course.category.toLowerCase() === activeFilter.toLowerCase());

  const modules = course?.modules || [];
  const requiredModules = modules.filter((module) => module.isRequired);
  const selectedModule = modules.find(
    (module) => (module.id || module.unitKey) === selectedModuleId
  );
  const completedLearningUnitIds = new Set(
    progressRows
      .filter((row) => row.status === 'completed')
      .map((row) => row.learning_unit_id)
  );
  const completedCount = requiredModules.filter((module) => (
    completedLearningUnitIds.has(unitIdsByKey[module.unitKey])
  )).length;
  const moduleWithDatabaseId = selectedModule
    ? { ...selectedModule, learningUnitId: unitIdsByKey[selectedModule.unitKey] }
    : null;

  const handleModuleSelect = (moduleId) => {
    if (moduleId === null) {
      setIsModuleOpen(false);
      setSelectedModuleId(null);
      return;
    }

    const targetModule = modules.find(
      (module) => (module.id || module.unitKey) === moduleId
    );

    if (targetModule) {
      setSelectedModuleId(moduleId);
      setIsModuleOpen(true);
      return;
    }

    const currentIndex = modules.findIndex(
      (module) => (module.id || module.unitKey) === selectedModuleId
    );
    setIsModuleOpen(true);
    setSelectedModuleId(
      currentIndex >= 0 && currentIndex < modules.length - 1
        ? modules[currentIndex + 1].id || modules[currentIndex + 1].unitKey
        : null
    );
  };

  async function tryCompleteEnrollment(progress) {
    if (
      !enrollment ||
      enrollment.enrollmentStatus !== 'active' ||
      completionRecord ||
      completionRpcLock.current ||
      requiredModules.length !== REQUIRED_MODULE_COUNT
    ) return;

    const completedIds = new Set(
      progress
        .filter((row) => row.status === 'completed')
        .map((row) => row.learning_unit_id)
    );
    const allRequiredModulesCompleted = requiredModules.every(
      (module) => completedIds.has(unitIdsByKey[module.unitKey])
    );

    if (!allRequiredModulesCompleted) return;

    completionRpcLock.current = true;

    try {
      await completeEnrollment(enrollment.enrollmentId);
      const authoritativeCompletion = await getEnrollmentCompletion(
        enrollment.enrollmentId
      );

      if (!authoritativeCompletion) {
        throw new Error(
          'Fullføringsbekreftelsen ble ikke funnet etter at kurset ble sendt til Academy.'
        );
      }

      setCompletionRecord(authoritativeCompletion);
      setEnrollment((current) => current
        ? { ...current, enrollmentStatus: 'completed' }
        : current);
      setCompletionError(null);
    } catch (error) {
      setCompletionError(completionErrorMessage(error));
    } finally {
      completionRpcLock.current = false;
    }
  }

  useEffect(() => {
    if (
      progressStatus !== 'ready' ||
      completionStatus !== 'ready' ||
      !enrollment ||
      completionRecord
    ) return;

    void tryCompleteEnrollment(progressRows);
  }, [
    progressStatus,
    completionStatus,
    enrollment,
    progressRows,
    completionRecord,
    unitIdsByKey
  ]);

  async function handleCompleteUnit(learningUnitId) {
    if (
      completionLock.current ||
      !enrollment ||
      enrollment.enrollmentStatus !== 'active' ||
      !learningUnitId
    ) return;

    completionLock.current = true;
    setBusyUnitId(learningUnitId);
    setCompletionError(null);

    try {
      await completeLearningUnit({
        enrollmentId: enrollment.enrollmentId,
        courseVersionId: enrollment.courseVersionId,
        learningUnitId
      });

      const refreshedRows = await getEnrollmentUnitProgress({
        enrollmentId: enrollment.enrollmentId,
        courseVersionId: enrollment.courseVersionId
      });
      setProgressRows(refreshedRows);
      await tryCompleteEnrollment(refreshedRows);
    } catch (error) {
      setCompletionError(completionErrorMessage(error));
    } finally {
      completionLock.current = false;
      setBusyUnitId(null);
    }
  }

  async function handleIssueCertificate() {
    if (
      !canIssueCertificate ||
      !completionRecord?.id ||
      certificateIssueLock.current
    ) return;

    certificateIssueLock.current = true;
    setCertificateIssueBusy(true);
    setCertificateError('');

    try {
      const existingCertificate = await getCertificateForCompletion(
        completionRecord.id
      );

      if (existingCertificate) {
        setCertificate(existingCertificate);
        setCertificateStatus('ready');
        return;
      }

      const issuance = await issueCertificate(completionRecord.id);
      setCertificate(issuance.certificate);
      setVerificationSecret(issuance.issuance.verification_secret);
      setCertificateStatus('ready');
    } catch (error) {
      try {
        const authoritativeCertificate = await getCertificateForCompletion(
          completionRecord.id
        );

        if (authoritativeCertificate) {
          setCertificate(authoritativeCertificate);
          setCertificateStatus('ready');
          return;
        }
      } catch (readError) {
        setCertificateError(
          readError?.message || error?.message || 'Kunne ikke lese kursbeviset etter utstedelse.'
        );
        setCertificateStatus('error');
        return;
      }

      setCertificateError(error?.message || 'Kursbeviset kunne ikke utstedes.');
      setCertificateStatus('ready');
    } finally {
      certificateIssueLock.current = false;
      setCertificateIssueBusy(false);
    }
  }

  function openCertificateView() {
    setCertificatePrintError('');
    setCertificateViewOpen(true);
  }

  function printCertificate() {
    setCertificatePrintError('');
    printAcademyCertificate(certificate, setCertificatePrintError);
  }

  function closeCertificateView() {
    setCertificateViewOpen(false);
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB] pb-24">
      <Navbar />

      <section className="mx-auto max-w-7xl px-4 pt-28 sm:px-6 lg:px-8">
        {courseStatus === 'loading' && (
          <p className="rounded-2xl border border-slate-200 bg-white p-5 text-slate-600">
            Laster kursinnhold …
          </p>
        )}

        {courseStatus === 'error' && (
          <div className="rounded-2xl border border-red-200 bg-white p-5">
            <p className="font-semibold text-red-700">Kunne ikke laste kursinnholdet.</p>
            {courseError && (
              <p className="mt-2 text-sm text-slate-500">{courseError}</p>
            )}
          </div>
        )}

        {courseStatus === 'success' && !course && (
          <p className="rounded-2xl border border-slate-200 bg-white p-5 text-slate-600">
            Kurset er ikke tilgjengelig.
          </p>
        )}

        {courseStatus === 'success' && course && (
          <div className="space-y-6">
            <CourseTabs activeTab={activeTab} onChange={setActiveTab} />

            {activeTab === 'course' && (
              <div className="space-y-6">
                {!isModuleOpen && (
                  <CourseOverview
                    course={course ? { ...course, modules: course.modules.map((module) => ({
                      ...module,
                      learningUnitId: unitIdsByKey[module.unitKey]
                    })) } : course}
                    selectedId={selectedModuleId}
                    onSelect={handleModuleSelect}
                    completedLearningUnitIds={completedLearningUnitIds}
                    progressStatus={progressStatus}
                    completedCount={completedCount}
                  />
                )}
                {isModuleOpen && moduleWithDatabaseId && (
                  <CourseModuleContent
                    module={moduleWithDatabaseId}
                    onSelect={handleModuleSelect}
                    isCompleted={completedLearningUnitIds.has(moduleWithDatabaseId.learningUnitId)}
                    onComplete={handleCompleteUnit}
                    completionBusy={busyUnitId === moduleWithDatabaseId.learningUnitId}
                    completionError={completionError}
                    progressAvailable={
                      progressStatus === 'ready' &&
                      enrollment?.enrollmentStatus === 'active' &&
                      !completionRecord
                    }
                  />
                )}
                {completionRecord && (
                  <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-900">
                    <h2 className="font-bold">Kurset er fullført</h2>
                    <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                      <div>
                        <dt className="font-semibold">Fullført:</dt>
                        <dd>{formatCompletionDate(completionRecord.completed_at)}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold">Fullføringsmetode:</dt>
                        <dd>{completionRecord.completion_method}</dd>
                      </div>
                    </dl>
                    {certificate && (
                      <div className="mt-5 border-t border-emerald-200 pt-4">
                        <h3 className="font-bold">
                          {certificate.status === 'revoked'
                            ? 'Kursbevis tilbakekalt'
                            : 'Kursbevis utstedt'}
                        </h3>
                        <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                          <div>
                            <dt className="font-semibold">Sertifikatnummer:</dt>
                            <dd>{certificate.certificate_number}</dd>
                          </div>
                          <div>
                            <dt className="font-semibold">Utstedt:</dt>
                            <dd>{formatCompletionDate(certificate.issued_at)}</dd>
                          </div>
                          <div>
                            <dt className="font-semibold">Gyldig til:</dt>
                            <dd>
                              {certificate.valid_until
                                ? formatCompletionDate(certificate.valid_until)
                                : 'Ingen utløpsdato'}
                            </dd>
                          </div>
                          <div>
                            <dt className="font-semibold">Status:</dt>
                            <dd>
                              {certificate.status === 'issued'
                                ? 'Utstedt'
                                : certificate.status === 'revoked'
                                  ? 'Tilbakekalt'
                                  : certificate.status}
                            </dd>
                          </div>
                        </dl>
                        <div className="mt-4 flex flex-wrap gap-3">
                          <button
                            type="button"
                            onClick={openCertificateView}
                            className="rounded-xl border border-blue-700 bg-white px-4 py-2.5 text-sm font-bold text-blue-800 transition hover:bg-blue-50"
                          >
                            Vis kursbevis
                          </button>
                          <button
                            type="button"
                            onClick={printCertificate}
                            className="rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-blue-800"
                          >
                            Skriv ut
                          </button>
                        </div>
                        {certificatePrintError && (
                          <p role="alert" className="mt-3 text-sm text-rose-800">
                            {certificatePrintError}
                          </p>
                        )}
                      </div>
                    )}
                    {certificateStatus === 'loading' && (
                      <p className="mt-4 text-sm">Kontrollerer kursbevis …</p>
                    )}
                    {canIssueCertificate &&
                      !certificate &&
                      certificateStatus === 'ready' && (
                        <button
                          type="button"
                          onClick={handleIssueCertificate}
                          disabled={certificateIssueBusy}
                          className="mt-5 rounded-xl bg-emerald-800 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-900 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {certificateIssueBusy ? 'Utsteder …' : 'Utsted kursbevis'}
                        </button>
                      )}
                    {certificateError && (
                      <p role="alert" className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                        {certificateError}
                      </p>
                    )}
                  </section>
                )}
                {completionError && (
                  <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                    {completionError}
                  </p>
                )}
                {progressStatus === 'error' && progressError && (
                  <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                    {progressError.message}
                  </p>
                )}
              </div>
            )}

            {activeTab === 'facilitator' && modules.length > 0 && (
              <CourseFacilitatorGuide
                modules={modules}
                selectedId={selectedModuleId}
                onSelect={handleModuleSelect}
              />
            )}

            {activeTab === 'assessment' && (
              <section className="rounded-2xl border border-slate-200 bg-white p-6">
                {course.assessment ? (
                  <>
                    <h2 className="text-xl font-bold text-slate-900">
                      {course.assessment.title}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      {course.assessment.description}
                    </p>
                    <dl className="mt-5 grid gap-4 text-sm sm:grid-cols-2">
                      <div>
                        <dt className="font-semibold text-slate-500">Beståttgrense</dt>
                        <dd className="mt-1 text-slate-900">
                          {course.assessment.passThreshold}%
                        </dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-slate-500">Maksimalt antall forsøk</dt>
                        <dd className="mt-1 text-slate-900">
                          {course.assessment.maxAttempts}
                        </dd>
                      </div>
                    </dl>
                    <p className="mt-5 rounded-xl bg-blue-50 p-4 text-sm leading-6 text-blue-900">
                      Kunnskapstesten gjennomføres og vurderes sikkert i Academy. Resultatet beregnes ikke lokalt i nettleseren.
                    </p>
                    <CourseKnowledgeCheck assessment={course.assessment} />
                  </>
                ) : (
                  <p className="text-slate-600">
                    Ingen publisert kunnskapstest er tilgjengelig.
                  </p>
                )}
              </section>
            )}
          </div>
        )}
      </section>

      {/* Hero Seksjon */}
      <div className="pt-32 pb-12 px-4 text-center max-w-4xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight mb-4">
          Kurs & Kompetanse
        </h1>
        <p className="text-lg text-slate-500 font-medium leading-relaxed">
          Hev kompetansen i din virksomhet med våre spesialiserte kurs utviklet av organisasjonspsykologer.
        </p>
      </div>

      {/* Filter-piller */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="flex flex-wrap justify-center gap-3">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveFilter(category)}
              className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-200 ${
                activeFilter === category
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                  : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200 shadow-sm hover:shadow'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Kurs Rutenett (Grid) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCourses.map((course) => (
            <div
              key={course.id}
              className="bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 flex flex-col group"
            >
              {/* Bilde med Kategori-badge */}
              <div className="relative h-56 w-full overflow-hidden bg-slate-100">
                <img
                  src={course.image}
                  alt={course.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4 bg-blue-600/95 backdrop-blur-sm text-white text-[10px] font-black px-3 py-1.5 rounded-lg tracking-widest uppercase shadow-sm">
                  {course.category}
                </div>
              </div>

              {/* Kort-innhold */}
              <div className="p-8 flex flex-col flex-grow">
                {/* Målgruppe & Pris */}
                <div className="flex justify-between items-center mb-4">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                    {course.audience}
                  </span>
                  <span className="text-sm font-black text-blue-600">
                    {course.price}
                  </span>
                </div>

                {/* Tittel & Beskrivelse */}
                <h3 className="text-xl font-bold text-slate-900 mb-3 leading-snug">
                  {course.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-8 flex-grow">
                  {course.description}
                </p>

                {/* Varighet & Format */}
                <div className="flex gap-6 mb-8 text-xs font-bold text-slate-400">
                  <span className="flex items-center gap-1.5">
                    {course.duration}
                  </span>
                  <span className="flex items-center gap-1.5">
                    {course.format}
                  </span>
                </div>

                {/* Handling-knapp */}
                <button className="w-full py-3.5 rounded-xl border-2 border-slate-100 text-slate-700 font-bold hover:border-blue-600 hover:text-blue-600 hover:bg-blue-50 transition-all text-sm">
                  Les mer & påmelding
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Tom tilstand hvis filteret ikke har noen kurs */}
        {filteredCourses.length === 0 && (
          <div className="text-center py-20">
            <p className="text-slate-400 font-medium text-lg">Ingen kurs funnet i denne kategorien enda.</p>
            <button
              onClick={() => setActiveFilter('Alle')}
              className="mt-4 text-blue-600 font-bold hover:underline"
            >
              Vis alle kurs
            </button>
          </div>
        )}
      </div>

      {certificateViewOpen && certificate && (
        <AcademyCertificateView
          certificate={certificate}
          onClose={closeCertificateView}
          onPrintFailure={setCertificatePrintError}
        />
      )}
    </div>
  );
};

export default Courses;